import React, { useMemo, useState } from "react";
import { FaCalendarAlt, FaPen, FaPlus, FaTrashAlt } from "react-icons/fa";
import { Badge, ErrorBox, Field, Options } from "./common";
import { errorMessage, fmtDate, fmtMoney, locationsToText, nullIfBlank, numOrNull, platformLabel } from "../dmApi";

const PLATFORMS_BY_CHANNEL = {
  SEM: ["Google Ads", "YouTube"],
  SMM: ["Meta", "WhatsApp Business", "LinkedIn", "YouTube"],
};
const PLACEMENTS = {
  Meta: ["Facebook", "Instagram", "Messenger", "Audience Network"],
  "Google Ads": ["Search", "Display", "Maps"],
  YouTube: ["In-stream", "Shorts", "Discovery"],
};

const blankToEmpty = (obj) => Object.fromEntries(Object.entries(obj || {}).map(([k, v]) => [k, v ?? ""]));

function CampaignForm({ meta, channels, initial, onSubmit, onCancel, saving }) {
  const [form, setForm] = useState(() => ({
    Campaign_Name: "",
    Channel: channels[0] || "SMM",
    Platform: PLATFORMS_BY_CHANNEL[channels[0] || "SMM"][0],
    Goal: "Leads",
    Budget_Per_Day: "",
    Start_Date: "",
    End_Date: "",
    Status: "Draft",
    External_Id: "",
    Notes: "",
    ...blankToEmpty(initial),
  }));
  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const setChannel = (e) => {
    const channel = e.target.value;
    setForm((p) => ({ ...p, Channel: channel, Platform: PLATFORMS_BY_CHANNEL[channel][0] }));
  };

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      Campaign_Name: form.Campaign_Name.trim(),
      Channel: form.Channel,
      Platform: form.Platform,
      Goal: form.Goal,
      Budget_Per_Day: numOrNull(form.Budget_Per_Day),
      Start_Date: nullIfBlank(form.Start_Date),
      End_Date: nullIfBlank(form.End_Date),
      Status: form.Status,
      External_Id: nullIfBlank(form.External_Id),
      Notes: nullIfBlank(form.Notes),
    });
  };

  return (
    <form onSubmit={submit} className="dmp-card">
      <div className="dmp-form-grid">
        <Field label="Campaign name *">
          <input required value={form.Campaign_Name} onChange={set("Campaign_Name")} placeholder="TheRidge-Cmpn1" />
        </Field>
        <Field label="Channel">
          <select value={form.Channel} onChange={setChannel}>
            <Options values={channels} />
          </select>
        </Field>
        <Field label="Platform">
          <select value={form.Platform} onChange={set("Platform")}>
            <Options values={PLATFORMS_BY_CHANNEL[form.Channel] || meta?.ad_platforms} format={platformLabel} />
          </select>
        </Field>
        <Field label="Goal">
          <select value={form.Goal} onChange={set("Goal")}>
            <Options values={meta?.goals} />
          </select>
        </Field>
        <Field label="Budget per day (₹)">
          <input type="number" min={0} step="0.01" value={form.Budget_Per_Day} onChange={set("Budget_Per_Day")} />
        </Field>
        <Field label="Start date">
          <input type="date" value={form.Start_Date} onChange={set("Start_Date")} />
        </Field>
        <Field label="End date">
          <input type="date" value={form.End_Date} onChange={set("End_Date")} />
        </Field>
        <Field label="Status">
          <select value={form.Status} onChange={set("Status")}>
            <Options values={meta?.run_statuses} />
          </select>
        </Field>
        <Field label="Campaign ID on platform">
          <input value={form.External_Id} onChange={set("External_Id")} />
        </Field>
        <Field label="Notes (audience, SEO/SEM keywords…)" full>
          <textarea rows={2} value={form.Notes} onChange={set("Notes")} />
        </Field>
      </div>
      <div className="dmp-form-actions">
        <button type="button" className="dmp-btn dmp-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="dmp-btn" disabled={saving}>
          {saving ? "Saving…" : "Save campaign"}
        </button>
      </div>
    </form>
  );
}

function AdForm({ meta, campaign, approved, initial, onSubmit, onCancel, saving }) {
  const [form, setForm] = useState(() => ({
    Ad_Set_Name: "",
    Ad_Name: "",
    Age_Min: "",
    Age_Max: "",
    Gender: "All",
    Budget_Per_Day: "",
    Start_Date: "",
    End_Date: "",
    Preview_URL: "",
    External_Id: "",
    Status: "Draft",
    ...blankToEmpty(initial),
    Placements: initial?.Placements || [],
    Locations: locationsToText(initial?.Locations),
    Content_Id: initial?.Content_Id ? String(initial.Content_Id) : "",
  }));
  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const togglePlacement = (value) =>
    setForm((p) => ({
      ...p,
      Placements: p.Placements.includes(value) ? p.Placements.filter((v) => v !== value) : [...p.Placements, value],
    }));

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      Ad_Set_Name: nullIfBlank(form.Ad_Set_Name),
      Ad_Name: form.Ad_Name.trim(),
      Content_Id: numOrNull(form.Content_Id),
      Placements: form.Placements,
      Locations: form.Locations,
      Age_Min: numOrNull(form.Age_Min),
      Age_Max: numOrNull(form.Age_Max),
      Gender: form.Gender,
      Budget_Per_Day: numOrNull(form.Budget_Per_Day),
      Start_Date: nullIfBlank(form.Start_Date),
      End_Date: nullIfBlank(form.End_Date),
      Preview_URL: nullIfBlank(form.Preview_URL),
      External_Id: nullIfBlank(form.External_Id),
      Status: form.Status,
    });
  };

  const placementOptions = PLACEMENTS[campaign.Platform] || [];
  return (
    <form onSubmit={submit} className="dmp-card" style={{ background: "var(--dmp-soft)" }}>
      <div className="dmp-form-grid">
        {campaign.Platform === "Meta" ? (
          <Field label="Ad set name">
            <input value={form.Ad_Set_Name} onChange={set("Ad_Set_Name")} placeholder="TheRidge-Adset1" />
          </Field>
        ) : null}
        <Field label="Ad name *">
          <input required value={form.Ad_Name} onChange={set("Ad_Name")} placeholder="TheRidge-Ad1" />
        </Field>
        <Field label="Ad content (approved)">
          <select value={form.Content_Id} onChange={set("Content_Id")}>
            <option value="">None</option>
            {approved.map((c) => (
              <option key={c.Content_Id} value={c.Content_Id}>
                {c.Content_Type}: {c.Title}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Status">
          <select value={form.Status} onChange={set("Status")}>
            <Options values={meta?.run_statuses} />
          </select>
        </Field>
        <Field label="Age min">
          <input type="number" min={13} max={65} value={form.Age_Min} onChange={set("Age_Min")} />
        </Field>
        <Field label="Age max">
          <input type="number" min={13} max={65} value={form.Age_Max} onChange={set("Age_Max")} />
        </Field>
        <Field label="Gender">
          <select value={form.Gender} onChange={set("Gender")}>
            <Options values={meta?.genders} />
          </select>
        </Field>
        <Field label="Budget per day (₹)">
          <input
            type="number"
            min={0}
            step="0.01"
            value={form.Budget_Per_Day}
            onChange={set("Budget_Per_Day")}
            placeholder={campaign.Budget_Per_Day != null ? `Campaign: ${campaign.Budget_Per_Day}` : ""}
          />
        </Field>
        <Field label="Start date">
          <input type="date" value={form.Start_Date} onChange={set("Start_Date")} />
        </Field>
        <Field label="End date">
          <input type="date" value={form.End_Date} onChange={set("End_Date")} />
        </Field>
        {placementOptions.length ? (
          <Field label="Placements" full>
            <div className="dmp-check-row">
              {placementOptions.map((p) => (
                <label key={p}>
                  <input type="checkbox" checked={form.Placements.includes(p)} onChange={() => togglePlacement(p)} />
                  {p}
                </label>
              ))}
            </div>
          </Field>
        ) : null}
        <Field label="Locations (comma separated, radius e.g. LB Nagar (R2km))" full>
          <input value={form.Locations} onChange={set("Locations")} placeholder="Dhandumailaram (R45km), Vijayawada (R45km)" />
        </Field>
        <Field label="Ad preview link">
          <input value={form.Preview_URL} onChange={set("Preview_URL")} placeholder="https://fb.me/…" />
        </Field>
        <Field label="Ad / post ID on platform">
          <input value={form.External_Id} onChange={set("External_Id")} />
        </Field>
      </div>
      <div className="dmp-form-actions">
        <button type="button" className="dmp-btn dmp-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="dmp-btn" disabled={saving}>
          {saving ? "Saving…" : "Save ad"}
        </button>
      </div>
    </form>
  );
}

/** Paid campaigns (SEM: Google Ads / SMM: Meta Ads) with their ad sets / ads. */
function CampaignTable({ api, meta, projectId, campaigns, content, access, onChange }) {
  const [editing, setEditing] = useState(null); // {type: 'campaign'|'ad', campaign?, item?}
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const approved = useMemo(() => content.filter((c) => c.Review_Status === "Approved"), [content]);
  const channels = (meta?.channels || ["SEM", "SMM"]).filter((c) => (access?.services || []).includes(c));
  const contentTitle = (id) => content.find((c) => c.Content_Id === id)?.Title;

  const run = async (fn) => {
    setError("");
    try {
      await fn();
      await onChange();
      return true;
    } catch (err) {
      setError(errorMessage(err));
      return false;
    }
  };

  const saveCampaign = async (body) => {
    setSaving(true);
    const ok = await run(() =>
      editing.item ? api.updateCampaign(editing.item.Campaign_Id, body) : api.createCampaign(projectId, body)
    );
    setSaving(false);
    if (ok) setEditing(null);
  };

  const saveAd = async (body) => {
    setSaving(true);
    const ok = await run(() =>
      editing.item ? api.updateAd(editing.item.Ad_Id, body) : api.createAd(editing.campaign.Campaign_Id, body)
    );
    setSaving(false);
    if (ok) setEditing(null);
  };

  const isEditing = (type, id) => editing?.type === type && (editing.item?.[type === "ad" ? "Ad_Id" : "Campaign_Id"] ?? null) === id;

  return (
    <>
      <div className="dmp-header dmp-header--section" style={{ marginBottom: 10 }}>
        <button type="button" className="dmp-btn" onClick={() => setEditing({ type: "campaign" })} disabled={!channels.length}>
          New campaign
        </button>
      </div>
      <ErrorBox message={error} />
      {editing?.type === "campaign" && !editing.item ? (
        <CampaignForm meta={meta} channels={channels} saving={saving} onSubmit={saveCampaign} onCancel={() => setEditing(null)} />
      ) : null}

      {campaigns.length === 0 ? <div className="dmp-card dmp-empty">No campaigns yet.</div> : null}

      {campaigns.map((c) => (
        <div key={c.Campaign_Id} className="dmp-campaign">
          <div className="dmp-campaign__head">
            <div className="dmp-campaign__top">
              <div className="dmp-campaign__title">
                <strong className="dmp-campaign__name">{c.Campaign_Name}</strong>
                <Badge value={c.Status} />
              </div>
              <div className="dmp-citem__icons">
                <button
                  type="button"
                  className="dmp-citem__icon"
                  title="Edit campaign"
                  aria-label="Edit campaign"
                  onClick={() => setEditing({ type: "campaign", item: c })}
                >
                  <FaPen />
                </button>
                <button
                  type="button"
                  className="dmp-citem__icon dmp-citem__icon--danger"
                  title="Delete campaign"
                  aria-label="Delete campaign"
                  onClick={() => window.confirm(`Delete campaign "${c.Campaign_Name}" and its ads?`) && run(() => api.deleteCampaign(c.Campaign_Id))}
                >
                  <FaTrashAlt />
                </button>
              </div>
            </div>
            <div className="dmp-campaign__channel">
              <span className="dmp-citem__type">{c.Channel}</span>
              <span>{platformLabel(c.Platform)}</span>
            </div>
            <dl className="dmp-campaign__stats">
              <div>
                <dt>Goal</dt>
                <dd>{c.Goal || "—"}</dd>
              </div>
              <div>
                <dt>Budget / day</dt>
                <dd>{fmtMoney(c.Budget_Per_Day)}</dd>
              </div>
              <div>
                <dt>CRM leads</dt>
                <dd>{c.Crm_Leads ?? 0}</dd>
              </div>
            </dl>
            <div className="dmp-campaign__dates">
              <span>
                <FaCalendarAlt aria-hidden="true" />
                {fmtDate(c.Start_Date)} → {fmtDate(c.End_Date)}
              </span>
              {c.Expires_On ? <span>Expires {fmtDate(c.Expires_On)}</span> : null}
              {c.Published_On ? (
                <span>
                  Published {fmtDate(c.Published_On)}
                  {c.Published_By_Name ? ` by ${c.Published_By_Name}` : ""}
                </span>
              ) : null}
            </div>
          </div>
          <div className="dmp-campaign__body">
            {isEditing("campaign", c.Campaign_Id) ? (
              <CampaignForm
                meta={meta}
                channels={channels.includes(c.Channel) ? channels : [c.Channel, ...channels]}
                initial={c}
                saving={saving}
                onSubmit={saveCampaign}
                onCancel={() => setEditing(null)}
              />
            ) : null}
            {editing?.type === "ad" && !editing.item && editing.campaign?.Campaign_Id === c.Campaign_Id ? (
              <AdForm meta={meta} campaign={c} approved={approved} saving={saving} onSubmit={saveAd} onCancel={() => setEditing(null)} />
            ) : null}
            <div className="dmp-campaign__ads-head">
              <span>
                Ads <span className="dmp-board__count">{c.Ads.length}</span>
              </span>
              <button type="button" className="dmp-chip-btn" onClick={() => setEditing({ type: "ad", campaign: c })}>
                <FaPlus aria-hidden="true" />
                Add ad
              </button>
            </div>
            {c.Ads.length === 0 ? (
              <div className="dmp-campaign__empty">No ads yet.</div>
            ) : (
              <div className="dmp-table-wrap">
                <table className="dmp-table dmp-ads-table">
                  <thead>
                    <tr>
                      <th>Ad set / ad</th>
                      <th>Locations</th>
                      <th>Audience</th>
                      <th className="dmp-num">Budget/day</th>
                      <th>Dates</th>
                      <th>Content</th>
                      <th>Preview</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {c.Ads.map((ad) =>
                      isEditing("ad", ad.Ad_Id) ? (
                        <tr key={ad.Ad_Id} className="dmp-ads-table__form-row">
                          <td colSpan={9}>
                            <AdForm
                              meta={meta}
                              campaign={c}
                              approved={approved}
                              initial={ad}
                              saving={saving}
                              onSubmit={saveAd}
                              onCancel={() => setEditing(null)}
                            />
                          </td>
                        </tr>
                      ) : (
                        <tr key={ad.Ad_Id}>
                          <td className="dmp-ads-table__name">
                            {ad.Ad_Set_Name ? <div className="dmp-muted">{ad.Ad_Set_Name}</div> : null}
                            <strong>{ad.Ad_Name}</strong>
                            {(ad.Placements || []).length ? <div className="dmp-muted">{ad.Placements.join(", ")}</div> : null}
                          </td>
                          <td data-label="Locations" className="dmp-ads-table__locations">
                            {locationsToText(ad.Locations) || <span className="dmp-muted">—</span>}
                          </td>
                          <td data-label="Audience">
                            {ad.Age_Min || ad.Age_Max ? `${ad.Age_Min || 13}–${ad.Age_Max || 65}` : "All ages"}
                            <div className="dmp-muted">{ad.Gender}</div>
                          </td>
                          <td data-label="Budget/day" className="dmp-num">{fmtMoney(ad.Budget_Per_Day)}</td>
                          <td data-label="Dates" className="dmp-ads-table__dates">
                            {fmtDate(ad.Start_Date)} → {fmtDate(ad.End_Date)}
                          </td>
                          <td data-label="Content">{contentTitle(ad.Content_Id) || <span className="dmp-muted">—</span>}</td>
                          <td data-label="Preview">
                            {ad.Preview_URL ? (
                              <a href={ad.Preview_URL} target="_blank" rel="noreferrer">
                                Open
                              </a>
                            ) : (
                              <span className="dmp-muted">—</span>
                            )}
                            {ad.External_Id ? <div className="dmp-muted">{ad.External_Id}</div> : null}
                          </td>
                          <td data-label="Status">
                            <Badge value={ad.Status} />
                          </td>
                          <td className="dmp-ads-table__actions">
                            <div className="dmp-item__actions" style={{ marginTop: 0 }}>
                              <button type="button" className="dmp-link" onClick={() => setEditing({ type: "ad", campaign: c, item: ad })}>
                                Edit
                              </button>
                              <button
                                type="button"
                                className="dmp-link"
                                style={{ color: "var(--dmp-danger)" }}
                                onClick={() => window.confirm(`Delete ad "${ad.Ad_Name}"?`) && run(() => api.deleteAd(ad.Ad_Id))}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ))}
    </>
  );
}

export default CampaignTable;
